import { AppContent, NavigationConfig, FormStep } from '../types';

export const navigationConfig: NavigationConfig = {
  brand: {
    name: 'DevDriven'
  },
  items: [
    { id: 'home', label: 'Início', href: '#home' },
    { id: 'about', label: 'Sobre', href: '#about' },
    { id: 'services', label: 'Serviços', href: '#services' },
    { id: 'contact', label: 'Contato', href: '#contact' }
  ],
  cta: {
    text: 'Solicitar Orçamento',
    href: '#simulator'
  }
};

export const appContent: AppContent = {
  hero: {
    title: 'Soluções Digitais Sem Burocracia',
    subtitle: 'Conectamos você a uma equipe de especialistas para criar seu site, aplicativo ou sistema de forma mais rápida, eficiente e transparente.',
    ctaText: 'Solicitar Orçamento Personalizado'
  },
  problem: {
    title: 'O Problema que Você Enfrenta',
    subtitle: 'Você já se deparou com essas situações?',
    description: 'Muitas empresas enfrentam obstáculos que impedem o crescimento digital. Identificamos os principais desafios e oferecemos soluções eficazes.',
    items: [
      {
        id: 'outdated-systems',
        icon: '⚠️',
        title: 'Sistemas Desatualizados',
        description: 'Tecnologias antigas que limitam a produtividade e competitividade da empresa.'
      },
      {
        id: 'slow-processes',
        icon: '🐌',
        title: 'Processos Manuais Lentos',
        description: 'Tarefas repetitivas que consomem tempo valioso dos colaboradores.'
      },
      {
        id: 'integration-issues',
        icon: '🔗',
        title: 'Falta de Integração',
        description: 'Sistemas isolados que não se comunicam, gerando retrabalho e inconsistências.'
      },
      {
        id: 'scalability-problems',
        icon: '📈',
        title: 'Dificuldade de Escalar',
        description: 'Infraestrutura que não acompanha o crescimento do negócio.'
      }
    ]
  },
  valueProposition: {
    title: 'Nossa Solução',
    subtitle: 'Especialistas que unem conhecimento em tecnologia, visão de negócio e capacidade de gestão',
    description: 'Profissionais multidisciplinares que eliminam a burocracia e entregam resultados excepcionais.',
    items: [
      {
        id: 'hybrid-competencies',
        icon: '💡',
        title: 'Competências Híbridas',
        description: 'Profissionais que analisam requisitos, definem arquitetura, gerem prioridades e codificam com excelência.'
      },
      {
        id: 'noise-elimination',
        icon: '🔧',
        title: 'Eliminação de Ruídos',
        description: 'Sem intermediários desnecessários. Comunicação direta com quem realmente constrói sua solução.'
      },
      {
        id: 'impact-decisions',
        icon: '📈',
        title: 'Decisões de Impacto',
        description: 'Tomamos decisões de negócio baseadas em impacto real, entregando soluções funcionais rapidamente.'
      }
    ]
  },
  benefits: {
    title: 'Resultados Comprovados',
    subtitle: 'Veja o que você ganha ao eliminar a burocracia do desenvolvimento',
    items: [
      {
        id: 'cost-reduction',
        icon: '60%',
        title: 'Redução de custos',
        description: 'Eliminação de intermediários e estruturas desnecessárias.'
      },
      {
        id: 'delivery-speed',
        icon: '3x',
        title: 'Velocidade de entrega',
        description: 'Ciclos mais rápidos sem burocracia e ruídos de comunicação.'
      },
      {
        id: 'value-focus',
        icon: '100%',
        title: 'Foco em valor',
        description: 'Todo esforço direcionado para o que realmente agrega valor ao seu negócio.'
      },
      {
        id: 'zero-bureaucracy',
        icon: '0',
        title: 'Burocracia',
        description: 'Comunicação direta e processos enxutos para máxima eficiência.'
      }
    ]
  },
  howItWorks: {
    title: 'Como Funciona Nossa Metodologia',
    items: [
      {
        id: 'discovery',
        icon: '🔍',
        title: '1. Descoberta e Análise',
        description: 'Mapeamos suas necessidades, processos atuais e objetivos de negócio para criar a estratégia ideal.'
      },
      {
        id: 'planning',
        icon: '📋',
        title: '2. Planejamento Estratégico',
        description: 'Definimos arquitetura, tecnologias e cronograma detalhado com marcos claros de entrega.'
      },
      {
        id: 'development',
        icon: '⚡',
        title: '3. Desenvolvimento Ágil',
        description: 'Implementação iterativa com entregas frequentes e feedback constante para máxima eficiência.'
      },
      {
        id: 'deployment',
        icon: '🚀',
        title: '4. Deploy e Suporte',
        description: 'Lançamento seguro com monitoramento contínuo e suporte especializado.'
      }
    ]
  },
  metrics: {
    title: 'Resultados que Falam por Si',
    subtitle: 'Números que comprovam nossa excelência',
    items: [
      {
        id: 'projects-delivered',
        value: '200+',
        label: 'Projetos Entregues',
        color: 'text-teal-400'
      },
      {
        id: 'client-satisfaction',
        value: '98%',
        label: 'Satisfação dos Clientes',
        color: 'text-sky-500'
      },
      {
        id: 'delivery-time',
        value: '-50%',
        label: 'Redução no Tempo de Entrega',
        color: 'text-yellow-400'
      },
      {
        id: 'cost-savings',
        value: '40%',
        label: 'Economia em Custos',
        color: 'text-green-400'
      }
    ]
  },
  mission: {
    title: 'Nossa Missão',
    subtitle: 'Acreditamos que a tecnologia deve servir ao propósito humano',
    description: 'Na DevDriven, não apenas desenvolvemos software - criamos pontes entre ideias e realidade. Acreditamos que cada linha de código deve ter um propósito, cada funcionalidade deve resolver um problema real, e cada projeto deve gerar impacto positivo. Nossa missão é democratizar o acesso à tecnologia de ponta, tornando possível para empresas de todos os tamanhos transformarem suas visões em soluções digitais excepcionais. Somos movidos pela paixão de ver nossos clientes prosperarem através da inovação tecnológica.'
  },
  faq: {
    title: 'Perguntas Frequentes',
    subtitle: 'Tire suas dúvidas sobre nossos serviços',
    items: [
      {
        id: 'what-we-do',
        question: 'Que tipo de projetos vocês desenvolvem?',
        answer: 'Desenvolvemos uma ampla gama de soluções: websites institucionais e e-commerce, sistemas web complexos, aplicativos mobile (iOS/Android), APIs e backends robustos, e soluções de automação. Nossa expertise abrange desde projetos simples até sistemas enterprise de grande escala.'
      },
      {
        id: 'timeline',
        question: 'Qual é o prazo típico para desenvolvimento?',
        answer: 'Os prazos variam conforme a complexidade: websites simples (2-4 semanas), e-commerce completo (6-12 semanas), sistemas web (8-20 semanas), apps mobile (10-16 semanas). Sempre fornecemos cronogramas detalhados após a análise inicial do projeto.'
      },
      {
        id: 'process',
        question: 'Como funciona o processo de desenvolvimento?',
        answer: 'Seguimos uma metodologia ágil: 1) Descoberta e análise das necessidades, 2) Planejamento e prototipação, 3) Desenvolvimento iterativo com entregas semanais, 4) Testes rigorosos, 5) Deploy e treinamento. Você acompanha tudo em tempo real através de nossa plataforma.'
      },
      {
        id: 'support',
        question: 'Vocês oferecem suporte após o lançamento?',
        answer: 'Sim! Oferecemos diferentes planos de suporte: desde manutenção básica até suporte técnico completo 24/7. Também fornecemos treinamento para sua equipe e documentação detalhada de todos os sistemas desenvolvidos.'
      },
      {
        id: 'technologies',
        question: 'Quais tecnologias vocês utilizam?',
        answer: 'Trabalhamos com as tecnologias mais modernas e estáveis do mercado: React, Node.js, Python, React Native, Flutter, AWS, Docker, PostgreSQL, MongoDB, entre outras. Escolhemos sempre a stack mais adequada para cada projeto.'
      },
      {
        id: 'pricing',
        question: 'Como funciona o modelo de precificação?',
        answer: 'Oferecemos modelos flexíveis: projeto fechado (escopo bem definido), time dedicado (alocação por período), ou híbrido. Todos os orçamentos são transparentes, sem custos ocultos, e incluem garantia de qualidade.'
      }
    ]
  }
};

export const formSteps: FormStep[] = [
  {
    id: 1,
    title: 'Tipo de Projeto',
    description: 'Conte-nos sobre o que você precisa desenvolver',
    fields: [
      {
        name: 'projectType',
        type: 'select',
        label: 'Tipo de Projeto',
        required: true,
        options: [
          { value: 'website', label: 'Website Institucional' },
          { value: 'webapp', label: 'Aplicação Web' },
          { value: 'mobile', label: 'App Mobile' },
          { value: 'api', label: 'API/Backend' },
          { value: 'ecommerce', label: 'E-commerce' },
          { value: 'custom', label: 'Solução Customizada' }
        ]
      },
      {
        name: 'description',
        type: 'textarea',
        label: 'Descrição do Projeto',
        placeholder: 'Descreva brevemente o que você precisa...',
        required: true,
        validation: {
          minLength: 20,
          maxLength: 500
        }
      }
    ]
  },
  {
    id: 2,
    title: 'Cronograma e Orçamento',
    description: 'Defina suas expectativas de prazo e investimento',
    fields: [
      {
        name: 'timeline',
        type: 'select',
        label: 'Prazo Desejado',
        required: true,
        options: [
          { value: '1-2weeks', label: '1-2 semanas' },
          { value: '3-4weeks', label: '3-4 semanas' },
          { value: '1-2months', label: '1-2 meses' },
          { value: '3-6months', label: '3-6 meses' },
          { value: '6months+', label: 'Mais de 6 meses' }
        ]
      },
      {
        name: 'budget',
        type: 'select',
        label: 'Orçamento Estimado',
        required: true,
        options: [
          { value: '5k-15k', label: 'R$ 5.000 - R$ 15.000' },
          { value: '15k-30k', label: 'R$ 15.000 - R$ 30.000' },
          { value: '30k-50k', label: 'R$ 30.000 - R$ 50.000' },
          { value: '50k-100k', label: 'R$ 50.000 - R$ 100.000' },
          { value: '100k+', label: 'Acima de R$ 100.000' }
        ]
      }
    ]
  },
  {
    id: 3,
    title: 'Seus Dados',
    description: 'Para enviarmos a proposta personalizada',
    fields: [
      {
        name: 'contact.name',
        type: 'text',
        label: 'Nome Completo',
        required: true,
        validation: {
          minLength: 2
        }
      },
      {
        name: 'contact.email',
        type: 'email',
        label: 'E-mail',
        required: true
      },
      {
        name: 'contact.company',
        type: 'text',
        label: 'Empresa',
        required: false
      },
      {
        name: 'contact.phone',
        type: 'text',
        label: 'Telefone',
        placeholder: '(11) 99999-9999',
        required: false
      }
    ]
  }
];

export const simulationSection = {
  title: 'Solicite Seu Orçamento Personalizado',
  subtitle: 'Conte-nos sobre seu projeto e receba um orçamento detalhado e personalizado em até 24h',
  features: [
    {
      id: 'fast-accurate',
      icon: '⚡',
      title: 'Super Rápido e Fácil',
      description: 'Menos de 3 minutos para ter sua estimativa. Algoritmo baseado em centenas de projetos reais'
    },
    {
      id: 'totally-free',
      icon: '🎯',
      title: 'Totalmente Gratuito',
      description: 'Sem compromisso, sem cadastro obrigatório. Apenas informações valiosas para seu projeto'
    },
    {
      id: 'complete-breakdown',
      icon: '📊',
      title: 'Detalhamento Completo',
      description: 'Receba breakdown de custos, cronograma estimado e próximos passos'
    }
  ],
  ctaText: 'Quero Meu Orçamento Agora'
};

export const ctaSection = {
  title: 'Descubra Quanto Custará Seu Projeto',
  subtitle: 'Processo super fácil e gratuito. Conte-nos sobre seu projeto e receba um orçamento personalizado em até 24h.',
    ctaText: 'Receber Orçamento Personalizado'
};

export const footerContent = {
  copyright: `© ${new Date().getFullYear()} DevDriven. Todos os direitos reservados.`
};